import { useState, useEffect } from "react";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import { MapPin, LayoutDashboard, Building2, CreditCard, Users, Menu, LogOut, ChevronRight, Utensils, Rocket, Calendar as CalendarIcon, BarChart3, Settings, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true })
      .then(res => setStats(res.data))
      .catch(() => toast.error('Błąd'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div></div>;
  }

  if (!stats) return null;

  const o = stats.overview;
  const payments = stats.recent_payments || [];
  const cats = stats.categories || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Panel Administratora</h1>
        <p className="text-slate-500">Przegląd platformy lokalnie.pro</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card><CardContent className="p-6"><p className="text-sm text-slate-500">Firmy w katalogu</p><p className="text-3xl font-bold text-slate-900 mt-1">{o.total_businesses}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-slate-500">Landing pages</p><p className="text-3xl font-bold text-slate-900 mt-1">{o.total_landing_pages}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-slate-500">Aktywne subskrypcje</p><p className="text-3xl font-bold text-slate-900 mt-1">{o.active_subscriptions}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-slate-500">Przychód (PLN)</p><p className="text-3xl font-bold text-slate-900 mt-1">{o.total_revenue.toFixed(2)}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-slate-500">Użytkownicy</p><p className="text-3xl font-bold text-slate-900 mt-1">{o.total_users}</p></CardContent></Card>
        <Card><CardContent className="p-6"><p className="text-sm text-slate-500">Firmy bez strony</p><p className="text-3xl font-bold text-slate-900 mt-1">{o.needs_website}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Ostatnie płatności</CardTitle></CardHeader>
          <CardContent>
            {payments.length === 0 ? <p className="text-slate-500 text-sm">Brak płatności</p> : (
              <div className="space-y-3">
                {payments.slice(0, 5).map(function (p, i) {
                  return (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="text-sm font-medium">{p.metadata?.business_name || p.business_id}</p>
                        <p className="text-xs text-slate-500">{p.package_tier}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{p.amount} PLN</p>
                        <Badge variant={p.status === 'completed' ? 'default' : 'secondary'} className="text-xs">{p.status}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Kategorie firm</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {cats.map(function (c, i) {
                return (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm">{c._id}</span>
                    <Badge variant="secondary">{c.count}</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Klucze, ktorych brak oznacza ze cos widocznego dla klienta cicho nie dziala
// (platnosci/maile), zamiast tylko "opcjonalnej" integracji — pokazujemy je jako
// baner na kazdej stronie admina, nie tylko w Ustawieniach, zeby nie dalo sie przeoczyc.
const CRITICAL_SETTINGS = {
  STRIPE_API_KEY: "Stripe nieskonfigurowany — checkout dziala w trybie MOCK, klienci NIE sa obciazani prawdziwymi pieniedzmi.",
  RESEND_API_KEY: "Resend nieskonfigurowany — maile (formularz kontaktowy, powiadomienia) nie wysylaja sie.",
};

function MissingConfigBanner() {
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { withCredentials: true })
      .then((res) => {
        const gaps = Object.keys(CRITICAL_SETTINGS).filter((key) => !res.data?.[key]?.configured);
        setMissing(gaps);
      })
      .catch(() => {});
  }, []);

  if (missing.length === 0) return null;

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
      <div className="flex items-start gap-2 text-sm text-amber-800">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div className="space-y-0.5">
          {missing.map((key) => (
            <p key={key}>{CRITICAL_SETTINGS[key]} <Link to="/admin/settings" className="underline font-medium">Uzupełnij w Ustawieniach</Link></p>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      toast.error('Brak uprawnień administratora');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div></div>;
  if (!user?.is_admin) return null;

  const closeSidebar = () => setSidebarOpen(false);
  const handleLogout = async () => { await logout(); navigate('/'); };
  const showDashboard = location.pathname === '/admin' || location.pathname === '/admin/';

  const navLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Przegląd', active: showDashboard },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analityka', active: location.pathname.startsWith('/admin/analytics') },
    { to: '/admin/businesses', icon: Building2, label: 'Firmy', active: location.pathname.startsWith('/admin/businesses') },
    { to: '/admin/menu-scanner', icon: Utensils, label: 'Skaner Menu AI', active: location.pathname.startsWith('/admin/menu-scanner') },
    { to: '/admin/seo', icon: Rocket, label: 'SEO Autopilot', active: location.pathname.startsWith('/admin/seo') },
    { to: '/admin/social', icon: CalendarIcon, label: 'Kalendarz AI', active: location.pathname.startsWith('/admin/social') },
    { to: '/admin/payments', icon: CreditCard, label: 'Płatności', active: location.pathname.startsWith('/admin/payments') },
    { to: '/admin/leads', icon: Users, label: 'Leady', active: location.pathname.startsWith('/admin/leads') },
    { to: '/admin/settings', icon: Settings, label: 'Ustawienia', active: location.pathname.startsWith('/admin/settings') },
    { to: '/admin/legal', icon: FileText, label: 'Dokumenty prawne', active: location.pathname.startsWith('/admin/legal') },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={closeSidebar} />}

      <aside className={`fixed top-0 left-0 h-full w-64 bg-slate-900 text-white z-50 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-700">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center"><MapPin className="w-5 h-5 text-white" /></div>
              <span className="font-bold text-lg">lokalnie.pro</span>
            </Link>
            <Badge className="mt-2 bg-amber-500/20 text-amber-400">Panel Admina</Badge>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navLinks.map(function (item, idx) {
              const Icon = item.icon;
              return (
                <Link key={idx} to={item.to} onClick={closeSidebar} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${item.active ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
                  <Icon className="w-5 h-5" /><span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center"><span className="text-sm font-medium">{user?.name?.charAt(0) || 'A'}</span></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-slate-400 truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Wyloguj</Button>
          </div>
        </div>
      </aside>

      <div className="lg:ml-64">
        <header className="bg-white border-b sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <div className="flex items-center gap-2 text-sm text-slate-500"><Link to="/" className="hover:text-slate-900">lokalnie.pro</Link><ChevronRight className="w-4 h-4" /><span className="text-slate-900">Admin</span></div>
            <Link to="/"><Button variant="ghost" size="sm">Zobacz stronę</Button></Link>
          </div>
        </header>
        <MissingConfigBanner />
        <main className="p-4 sm:p-6 lg:p-8">{showDashboard ? <AdminDashboard /> : <Outlet />}</main>
      </div>
    </div>
  );
}
